import { z } from 'zod';
import { MOD_MAX } from './mods.js';
/**
 * zod mirrors of protocol/schemas/datachannel/*.json (and attributes/displays.v1.json).
 * The JSON Schema files are the source of truth; the contract test suite proves these agree.
 * Every object is `.strict()` — unknown keys are a violation, never partially interpreted (§3.5).
 */
const epoch = z.number().int().min(0);
const seq = z.number().int().min(1);
const coord = z.number().min(0).max(1);
const disp = z.string().min(1);
const btn = z.number().int().min(0).max(4);
const mods = z.number().int().min(0).max(MOD_MAX);
// ---- move.v1 ----------------------------------------------------------------
export const MoveV1 = z
    .object({
    v: z.literal(1),
    seq: z.number().int().min(0),
    disp,
    x: coord,
    y: coord,
})
    .strict();
// ---- input.v1 ---------------------------------------------------------------
const DisplayInfo = z
    .object({
    disp,
    w: z.number().int().min(1),
    h: z.number().int().min(1),
    gx: z.number(),
    gy: z.number(),
    scale: z.number().min(0.1),
    main: z.boolean(),
})
    .strict();
export const InputV1 = z.discriminatedUnion('t', [
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('mdown'), disp, x: coord, y: coord, btn, clicks: z.number().int().min(1).max(3) }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('mup'), disp, x: coord, y: coord, btn }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('mdrag'), disp, x: coord, y: coord }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('wheel'), disp, x: coord, y: coord, dx: z.number(), dy: z.number(), mods }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('kdown'), code: z.string().min(1), key: z.string(), mods, rep: z.boolean() }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('kup'), code: z.string().min(1), mods }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('text'), str: z.string().min(1).max(1024) }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('dswitch'), disp }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('reset') }).strict(),
    z.object({ v: z.literal(1), e: epoch, seq, t: z.literal('ping'), ts: z.number().int() }).strict(),
    // Host -> controller replies
    z.object({ v: z.literal(1), t: z.literal('hello'), e: epoch, displays: z.array(DisplayInfo).min(1) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('displays'), displays: z.array(DisplayInfo).min(1) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('pong'), ts: z.number().int(), hts: z.number().int() }).strict(),
    z.object({ v: z.literal(1), t: z.literal('err'), code: z.enum(['bad-schema', 'bad-epoch', 'bad-seq', 'rate', 'unmapped-key', 'unknown-disp', 'not-controller', 'paused']), seq: z.number().int().min(0).optional() }).strict(),
    z.object({ v: z.literal(1), t: z.literal('bye'), reason: z.enum(['revoked', 'timeout', 'disconnect', 'panic', 'lock', 'released']) }).strict(),
]);
// ---- clip.v1 ----------------------------------------------------------------
export const ClipV1 = z.discriminatedUnion('t', [
    z.object({ v: z.literal(1), t: z.literal('offer'), xid: z.string().min(1), mime: z.enum(['text/plain;charset=utf-8', 'image/png']), size: z.number().int().min(0).max(1048576), sha256: z.string().regex(/^[0-9a-f]{64}$/) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('accept'), xid: z.string().min(1) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('chunk'), xid: z.string().min(1), i: z.number().int().min(0), n: z.number().int().min(1), data: z.string().max(10924) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('done'), xid: z.string().min(1) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('cancel'), xid: z.string().min(1), reason: z.enum(['too-big', 'privacy', 'opt-out', 'user', 'checksum', 'busy']) }).strict(),
]);
// ---- chat.v1 ----------------------------------------------------------------
export const ChatV1 = z
    .object({
    v: z.literal(1),
    t: z.literal('msg'),
    id: z.string().uuid(),
    ts: z.number().int(),
    body: z.string().min(1).max(2000),
})
    .strict();
// ---- sys.v1 -----------------------------------------------------------------
export const SysV1 = z.discriminatedUnion('t', [
    z.object({ v: z.literal(1), t: z.literal('control'), target: z.string().nullable(), by: z.string().optional() }).strict(),
    z.object({ v: z.literal(1), t: z.literal('muted'), target: z.string(), by: z.string().optional() }).strict(),
    z.object({ v: z.literal(1), t: z.literal('kicked'), target: z.string(), reason: z.string().max(200).optional() }).strict(),
    z.object({ v: z.literal(1), t: z.literal('privacy'), on: z.boolean(), reason: z.enum(['admin', 'secure-input']) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('ending'), inSec: z.number().int().min(0), reason: z.enum(['admin', 'maintenance', 'host-offline', 'idle']) }).strict(),
    z.object({ v: z.literal(1), t: z.literal('error'), code: z.string(), msg: z.string().max(300).optional() }).strict(),
]);
// ---- hand.v1 ----------------------------------------------------------------
export const HandV1 = z
    .object({
    v: z.literal(1),
    kind: z.literal('hand'),
    state: z.enum(['up', 'down']),
    reason: z.enum(['speak', 'control']),
})
    .strict();
// ---- typing.v1 --------------------------------------------------------------
export const TypingV1 = z
    .object({
    v: z.literal(1),
    kind: z.literal('typing'),
    state: z.enum(['start', 'stop']),
})
    .strict();
// ---- media.v1 ---------------------------------------------------------------
export const MediaV1 = z
    .object({
    v: z.literal(1),
    event: z.enum(['capture_downscale', 'capture_restore']),
    res: z.tuple([z.number().int().min(1), z.number().int().min(1)]),
    reason: z.enum(['bandwidth']),
})
    .strict();
// ---- latency.v1 -------------------------------------------------------------
export const LatencyV1 = z.discriminatedUnion('t', [
    z.object({ v: z.literal(1), t: z.literal('frame'), counter: z.number().int().min(0), t_host: z.number() }).strict(),
    z.object({ v: z.literal(1), t: z.literal('sync'), id: z.number().int().min(0), t1: z.number(), t2: z.number().optional(), t3: z.number().optional() }).strict(),
]);
// ---- displays.v1 (participant attribute) -----------------------------------
export const DisplaysV1 = z
    .object({
    v: z.literal(1),
    displays: z
        .array(z
        .object({
        i: z.number().int().min(0),
        uuid: z.string().min(1),
        w: z.number().int().min(1),
        h: z.number().int().min(1),
        scale: z.number().min(0.1),
        main: z.boolean(),
        name: z.string(),
    })
        .strict())
        .min(1),
})
    .strict();
/** Map of topic name -> zod schema, used by the generic decode helper and tests. */
export const DATACHANNEL_SCHEMAS = {
    'move.v1': MoveV1,
    'input.v1': InputV1,
    'clip.v1': ClipV1,
    'chat.v1': ChatV1,
    'sys.v1': SysV1,
    'hand.v1': HandV1,
    'typing.v1': TypingV1,
    'media.v1': MediaV1,
    'latency.v1': LatencyV1,
};
//# sourceMappingURL=datachannel.js.map